import { createFileRoute } from '@tanstack/react-router';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { seo } from '@/seo/seo';

export const Route = createFileRoute('/_marketing/privacy')({
  head: () => ({
    meta: [
      ...seo({
        title: 'Privacy Policy | Listening',
        description:
          'Explore our Privacy Policy to see how Listening protects and manages your personal information. Your privacy and security are our top priorities.',
      }),
    ],
  }),
  component: () => (
    <>
      <NavBar />
      <div className='mx-auto max-w-[1296px] px-[10px] pt-[40px] text-[14px] sm:px-5 md:px-[140px]'>
        <h1 className='mt-5 mb-[10px] text-[2.375rem] leading-[1.15]'>
          Privacy Policy
        </h1>
        <div>
          <p className='mb-[10px]'>Last updated September 21, 2026</p>
          <p className='mb-[10px]'>
            This privacy notice for Listening.com ("Company," "we," "us," or
            "our"), describes how and why we might collect, store, use, and/or
            share ("process") your information when you use our services
            ("Services"), such as when you:
          </p>
          <p className='mb-[10px]'>
            Visit our website at listening.com, or any website of ours that
            links to this privacy notice. Use our browser extension. Engage with
            us in other related ways, including any sales, marketing, or events.
            Questions or concerns? Reading this privacy notice will help you
            understand your privacy rights and choices. If you do not agree with
            our policies and practices, please do not use our Services. If you
            still have any questions or concerns, please contact us at
            <a
              className='text-azure-455 underline'
              href='mailto:support@listening.com'
            >
              support@listening.com
            </a>
            .
          </p>
          <h2 className='my-[10px] text-[1.125rem] leading-normal font-bold'>
            SUMMARY OF KEY POINTS
          </h2>
          <p className='mb-[10px]'>
            This summary provides key points from our privacy notice, but you
            can find out more details about any of these topics by clicking the
            link following each key point or by using our table of contents
            below to find the section you are looking for. You can also go
            directly to our{' '}
            <a className='text-azure-455 underline' href='#table-of-contents'>
              table of contents
            </a>
            .
          </p>
          <p className='mb-[10px]'>
            What personal information do we process? When you visit, use, or
            navigate our Services, we may process personal information depending
            on how you interact with Listening.com and the Services, the choices
            you make, and the products and features you use.
            <a
              className='text-azure-455 underline'
              href='#1-what-info-do-we-collect'
            >
              Click here to learn more.
            </a>
          </p>
          <p className='mb-[10px]'>
            Do we process any sensitive personal information? We do not process
            sensitive personal information.
          </p>
          <p className='mb-[10px]'>
            Do we receive any information from third parties? We do not receive
            any information from third parties.
          </p>
          <p className='mb-[10px]'>
            How do we process your information? We process your information to
            provide, improve, and administer our Services, communicate with you,
            for security and fraud prevention, and to comply with law. We may
            also process your information for other purposes with your consent.
            We process your information only when we have a valid legal reason
            to do so.
            <a
              className='text-azure-455 underline'
              href='#2-how-we-process-information'
            >
              Click here to learn more.
            </a>
          </p>
          <p className='mb-[10px]'>
            In what situations and with which parties do we share personal
            information? We may share information in specific situations and
            with specific third parties.
            <a
              className='text-azure-455 underline'
              href='#4-when-and-with-whom-do-we-share'
            >
              Click here to learn more.
            </a>
          </p>
          <p className='mb-[10px]'>
            How do we keep your information safe? We have organizational and
            technical processes and procedures in place to protect your personal
            information. However, no electronic transmission over the internet
            or information storage technology can be guaranteed to be 100%
            secure, so we cannot promise or guarantee that hackers,
            cybercriminals, or other unauthorized third parties will not be able
            to defeat our security and improperly collect, access, steal, or
            modify your information.
            <a
              className='text-azure-455 underline'
              href='#8-how-do-we-keep-your-info-safe'
            >
              Click here to learn more.
            </a>
          </p>
          <p className='mb-[10px]'>
            What are your rights? Depending on where you are located
            geographically, the applicable privacy law may mean you have certain
            rights regarding your personal information.
            <a
              className='text-azure-455 underline'
              href='#10-what-are-your-privacy-rights'
            >
              Click here to learn more.
            </a>
          </p>
          <p className='mb-[10px]'>
            How do you exercise your rights? The easiest way to exercise your
            rights is by filling out our data subject request form available
            here: listening.com, or by contacting us. We will consider and act
            upon any request in accordance with applicable data protection laws.
          </p>
          <h2
            className='my-[10px] text-[1.125rem] leading-normal font-bold'
            id='table-of-contents'
          >
            TABLE OF CONTENTS
          </h2>
          <ol className='list-decimal pl-10'>
            <li>
              <a
                className='text-azure-455 underline'
                href='#1-what-info-do-we-collect'
              >
                WHAT INFORMATION DO WE COLLECT?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#2-how-we-process-information'
              >
                HOW DO WE PROCESS YOUR INFORMATION?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#3-what-legal-bases-do-we-rely-on'
              >
                WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL
                INFORMATION?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#4-when-and-with-whom-do-we-share'
              >
                WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#5-do-we-use-cookies-and-other-tracking-technologies'
              >
                DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#6-info-tranferred-internationally'
              >
                IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#7-how-long-do-we-keep-your-info'
              >
                HOW LONG DO WE KEEP YOUR INFORMATION?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#8-how-do-we-keep-your-info-safe'
              >
                HOW DO WE KEEP YOUR INFORMATION SAFE?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#9-do-we-collect-info-from-minors'
              >
                DO WE COLLECT INFORMATION FROM MINORS?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#10-what-are-your-privacy-rights'
              >
                WHAT ARE YOUR PRIVACY RIGHTS?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#11-controls-for-do-not-track'
              >
                CONTROLS FOR DO-NOT-TRACK FEATURES
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#12-do-california-residents-have-specifc-rights'
              >
                DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#13-do-we-make-updates-to-this-notice'
              >
                DO WE MAKE UPDATES TO THIS NOTICE?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#14-how-can-you-contact-us-about-this-notice'
              >
                HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
              </a>
            </li>
            <li>
              <a
                className='text-azure-455 underline'
                href='#15-how-can-you-review-update-or-delete'
              >
                HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
                YOU?
              </a>
              <br />
            </li>
          </ol>
          <br />

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>1.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='1-what-info-do-we-collect'
            >
              WHAT INFORMATION DO WE COLLECT?
            </h2>
          </div>

          <p className='mb-[10px]'>Personal information you disclose to us</p>
          <p className='mb-[10px]'>
            In Short: We collect personal information that you provide to us.
          </p>
          <p className='mb-[10px]'>
            We collect personal information that you voluntarily provide to us
            when you register on the Services, express an interest in obtaining
            information about us or our products and Services, when you
            participate in activities on the Services, or otherwise when you
            contact us.
          </p>
          <p className='mb-[10px]'>
            Personal Information Provided by You. The personal information that
            we collect depends on the context of your interactions with us and
            the Services, the choices you make, and the products and features
            you use. The personal information we collect may include the
            following:&nbsp;
          </p>
          <ul className='list-disc pl-10'>
            <li>names</li>
            <li>phone numbers</li>
            <li>email addresses</li>
            <li>usernames</li>
            <li>passwords</li>
            <li>contact preferences</li>
            <li>debit/credit card numbers</li>
            <li>contact or authentication data</li>
            <li>
              Sensitive Information. We do not process sensitive information
              <br />
            </li>
          </ul>
          <p className='mb-[10px]'>
            Payment Data. We may collect data necessary to process your payment
            if you make purchases, such as your payment instrument number (such
            as a credit card number), and the security code associated with your
            payment instrument. All payment data is stored by Stripe. You may
            find their privacy notice link(s) here:
            <a
              className='text-azure-455 underline'
              href='https://stripe.com/privacy'
            >
              https://stripe.com/privacy
            </a>
            .
          </p>
          <p className='mb-[10px]'>
            All personal information that you provide to us must be true,
            complete, and accurate, and you must notify us of any changes to
            such personal information.
          </p>
          <p className='mb-[10px]'>Information automatically collected</p>
          <p className='mb-[10px]'>
            In Short: Some information — such as your Internet Protocol (IP)
            address and/or browser and device characteristics — is collected
            automatically when you visit our Services.
          </p>
          <p className='mb-[10px]'>
            We automatically collect certain information when you visit, use, or
            navigate the Services. On its own, this information does not reveal
            your name or contact information, but when you are signed in it may
            be linked to your account. It may include device and usage
            information, such as your IP address, browser and device
            characteristics, operating system, language preferences, referring
            URLs, device name, country, location, information about how and when
            you use our Services, and other technical information. This
            information is primarily needed to maintain the security and
            operation of our Services, and for our internal analytics and
            reporting purposes.
          </p>
          <p className='mb-[10px]'>
            Like many businesses, we also collect information through cookies
            and similar technologies.
          </p>
          <p className='mb-[10px]'>The information we collect includes:</p>
          <p className='mb-[10px]'>
            Log and Usage Data. Log and usage data is service-related,
            diagnostic, usage, and performance information our servers
            automatically collect when you access or use our Services and which
            we record in log files. Depending on how you interact with us, this
            log data may include your IP address, device information, browser
            type, and settings and information about your activity in the
            Services (such as the date/time stamps associated with your usage,
            pages and files viewed, searches, and other actions you take such as
            which features you use), device event information (such as system
            activity, error reports (sometimes called "crash dumps"), and
            hardware settings).Device Data. We collect device data such as
            information about your computer, phone, tablet, or other device you
            use to access the Services. Depending on the device used, this
            device data may include information such as your IP address (or
            proxy server), device and application identification numbers,
            location, browser type, hardware model, Internet service provider
            and/or mobile carrier, operating system, and system configuration
            information.
          </p>
          <p className='mb-[10px]'>
            Browser Extension Data. When you use our browser extension, it
            records diagnostic and usage events, such as installation and
            updates, opening the extension, sign-in, subscription checks,
            playback requests and errors, how long you listen, which controls
            you use, and the voice you chose. When you start playback on a web
            page, the event includes that page's address without its query
            string or fragment. Each event carries a random identifier for your
            installation of the extension and, if you are signed in, is linked
            to your account. These events do not include the text you listen to,
            your sign-in credentials, or other page content. We use this
            information to diagnose playback failures and to understand how the
            extension is used.
          </p>
          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>2.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='2-how-we-process-information'
            >
              HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
          </div>
          <p className='mb-[10px]'>
            In Short: We process your information to provide, improve, and
            administer our Services, communicate with you, for security and
            fraud prevention, and to comply with law. We may also process your
            information for other purposes with your consent.
          </p>
          <p className='mb-[10px]'>
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:To
            facilitate account creation and authentication and otherwise manage
            user accounts. We may process your information so you can create and
            log in to your account, as well as keep your account in working
            order.
          </p>
          <p className='mb-[10px]'>
            To save or protect an individual's vital interest. We may process
            your information when necessary to save or protect an individual’s
            vital interest, such as to prevent harm.
          </p>
          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>3.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='3-what-legal-bases-do-we-rely-on'
            >
              WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
            </h2>
          </div>
          <p className='mb-[10px]'>
            In Short: We only process your personal information when we believe
            it is necessary and we have a valid legal reason (i.e., legal basis)
            to do so under applicable law, like with your consent, to comply
            with laws, to provide you with services to enter into or fulfill our
            contractual obligations, to protect your rights, or to fulfill our
            legitimate business interests.
          </p>
          <p className='mb-[10px]'>
            If you are located in the EU or UK, this section applies to you.
          </p>
          <p className='mb-[10px]'>
            The General Data Protection Regulation (GDPR) and UK GDPR require us
            to explain the valid legal bases we rely on in order to process your
            personal information. As such, we may rely on the following legal
            bases to process your personal information: Consent. We may process
            your information if you have given us permission (i.e., consent) to
            use your personal information for a specific purpose. You can
            withdraw your consent at any time.{' '}
            <a
              className='text-azure-455 underline'
              href='https://gdpr-info.eu/'
            >
              Learn more about GDPR
            </a>
            . Legal Obligations. We may process your information where we
            believe it is necessary for compliance with our legal obligations,
            such as to cooperate with a law enforcement body or regulatory
            agency, exercise or defend our legal rights, or disclose your
            information as evidence in litigation in which we are involved.
            Vital Interests. We may process your information where we believe it
            is necessary to protect your vital interests or the vital interests
            of a third party, such as situations involving potential threats to
            the safety of any person.
          </p>
          <p className='mb-[10px]'>
            If you are located in Canada, this section applies to you.
          </p>
          <p className='mb-[10px]'>
            We may process your information if you have given us specific
            permission (i.e., express consent) to use your personal information
            for a specific purpose, or in situations where your permission can
            be inferred (i.e., implied consent). You can withdraw your consent
            at any time.{' '}
            <a
              className='text-azure-455 underline'
              href='#15-how-can-you-review-update-or-delete'
            >
              Learn how to review, update, or delete your data
            </a>
            .
          </p>
          <p className='mb-[10px]'>
            In some exceptional cases, we may be legally permitted under
            applicable law to process your information without your consent,
            including, for example: If collection is clearly in the interests of
            an individual and consent cannot be obtained in a timely way.
          </p>
          <p className='mb-[10px]'>
            For investigations and fraud detection and prevention For business
            transactions provided certain conditions are met. If it is contained
            in a witness statement and the collection is necessary to assess,
            process, or settle an insurance claim. For identifying injured, ill,
            or deceased persons and communicating with next of kin. If we have
            reasonable grounds to believe an individual has been, is, or may be
            victim of financial abuse. If it is reasonable to expect collection
            and use with consent would compromise the availability or the
            accuracy of the information and the collection is reasonable for
            purposes related to investigating a breach of an agreement or a
            contravention of the laws of Canada or a province. If disclosure is
            required to comply with a subpoena, warrant, court order, or rules
            of the court relating to the production of records. If it was
            produced by an individual in the course of their employment,
            business, or profession and the collection is consistent with the
            purposes for which the information was produced. If the collection
            is solely for journalistic, artistic, or literary purposes. If the
            information is publicly available and is specified by the
            regulations
          </p>
          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>4.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='4-when-and-with-whom-do-we-share'
            >
              WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
          </div>
          <p className='mb-[10px]'>
            In Short: We may share information in specific situations described
            in this section and/or with the following third parties.
          </p>
          <p className='mb-[10px]'>
            We may need to share your personal information in the following
            situations:
          </p>
          <p className='mb-[10px]'>
            Business Transfers. We may share or transfer your information in
            connection with, or during negotiations of, any merger, sale of
            company assets, financing, or acquisition of all or a portion of our
            business to another company.
          </p>
          <p className='mb-[10px]'>
            Uploads to Listening are private by default. If you choose to share
            a document with a specific person, that document will be visible to
            that person. If you choose to make a content upload public, the
            content will be visible and available to anybody on the internet.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>5.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='5-do-we-use-cookies-and-other-tracking-technologies'
            >
              DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: We may use cookies and other tracking technologies to
            collect and store your information.
          </p>
          <p className='mb-[10px]'>
            We may use cookies and similar tracking technologies (like web
            beacons and pixels) to access or store information. Specific
            information about how we use such technologies and how you can
            refuse certain cookies is set out in our Cookie Notice.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>6.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='6-info-tranferred-internationally'
            >
              IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: We may transfer, store, and process your information in
            countries other than your own.
          </p>
          <p className='mb-[10px]'>
            Our servers are located in the United States. If you are accessing
            our Services from outside the United States, please be aware that
            your information may be transferred to, stored, and processed by us
            in our facilities and by those third parties with whom we may share
            your personal information (see "WHEN AND WITH WHOM DO WE SHARE YOUR
            PERSONAL INFORMATION?" above), in and other countries.
          </p>
          <p className='mb-[10px]'>
            If you are a resident in the European Economic Area (EEA) or United
            Kingdom (UK), then these countries may not necessarily have data
            protection laws or other similar laws as comprehensive as those in
            your country. However, we will take all necessary measures to
            protect your personal information in accordance with this privacy
            notice and applicable law.
          </p>
          <p className='mb-[10px]'>
            European Commission's Standard Contractual Clauses:
          </p>
          <p className='mb-[10px]'>
            We have implemented measures to protect your personal information,
            including by using the European Commission's Standard Contractual
            Clauses for transfers of personal information between our group
            companies and between us and our third-party providers. These
            clauses require all recipients to protect all personal information
            that they process originating from the EEA or UK in accordance with
            European data protection laws and regulations. Our Standard
            Contractual Clauses can be provided upon request. We have
            implemented similar appropriate safeguards with our third-party
            service providers and partners and further details can be provided
            upon request.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>7.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='7-how-long-do-we-keep-your-info'
            >
              HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: We keep your information for as long as necessary to
            fulfill the purposes outlined in this privacy notice unless
            otherwise required by law.
          </p>
          <p className='mb-[10px]'>
            We will only keep your personal information for as long as it is
            necessary for the purposes set out in this privacy notice, unless a
            longer retention period is required or permitted by law (such as
            tax, accounting, or other legal requirements). No purpose in this
            notice will require us keeping your personal information for longer
            than the period of time in which users have an account with us.
          </p>
          <p className='mb-[10px]'>
            When we have no ongoing legitimate business need to process your
            personal information, we will either delete or anonymize such
            information, or, if this is not possible (for example, because your
            personal information has been stored in backup archives), then we
            will securely store your personal information and isolate it from
            any further processing until deletion is possible.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>8.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='8-how-do-we-keep-your-info-safe'
            >
              HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: We aim to protect your personal information through a
            system of organizational and technical security measures.
          </p>
          <p className='mb-[10px]'>
            We have implemented appropriate and reasonable technical and
            organizational security measures designed to protect the security of
            any personal information we process. However, despite our safeguards
            and efforts to secure your information, no electronic transmission
            over the Internet or information storage technology can be
            guaranteed to be 100% secure, so we cannot promise or guarantee that
            hackers, cybercriminals, or other unauthorized third parties will
            not be able to defeat our security and improperly collect, access,
            steal, or modify your information. Although we will do our best to
            protect your personal information, transmission of personal
            information to and from our Services is at your own risk. You should
            only access the Services within a secure environment.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>9.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='9-do-we-collect-info-from-minors'
            >
              DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: We do not knowingly collect data from or market to
            children under 18 years of age.
          </p>
          <p className='mb-[10px]'>
            We do not knowingly solicit data from or market to children under 18
            years of age. By using the Services, you represent that you are at
            least 18 or that you are the parent or guardian of such a minor and
            consent to such minor dependent’s use of the Services. If we learn
            that personal information from users less than 18 years of age has
            been collected, we will deactivate the account and take reasonable
            measures to promptly delete such data from our records. If you
            become aware of any data we may have collected from children under
            age 18, please contact us at
            <a
              className='text-azure-455 underline'
              href='mailto:support@listening.com'
            >
              support@listening.com
            </a>
            .
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>10.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='10-what-are-your-privacy-rights'
            >
              WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: In some regions, such as the European Economic Area (EEA),
            United Kingdom (UK), and Canada, you have rights that allow you
            greater access to and control over your personal information. You
            may review, change, or terminate your account at any time.
          </p>
          <p className='mb-[10px]'>
            In some regions (like the EEA, UK, and Canada), you have certain
            rights under applicable data protection laws. These may include the
            right (i) to request access and obtain a copy of your personal
            information, (ii) to request rectification or erasure; (iii) to
            restrict the processing of your personal information; and (iv) if
            applicable, to data portability. In certain circumstances, you may
            also have the right to object to the processing of your personal
            information. You can make such a request by contacting us by using
            the contact details provided in the section "HOW CAN YOU CONTACT US
            ABOUT THIS NOTICE?" below.
          </p>
          <p className='mb-[10px]'>
            We will consider and act upon any request in accordance with
            applicable data protection laws.
          </p>
          <p className='mb-[10px]'>
            If you are located in the EEA or UK and you believe we are
            unlawfully processing your personal information, you also have the
            right to complain to your local data protection supervisory
            authority. You can find their contact details here:
            <a href='https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm'>
              https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
            </a>
            .
          </p>
          <p className='mb-[10px]'>
            If you are located in Switzerland, the contact details for the data
            protection authorities are available here:
            <a
              className='text-azure-455 underline'
              href='https://www.edoeb.admin.ch/edoeb/en/home.html'
            >
              https://www.edoeb.admin.ch/edoeb/en/home.html
            </a>
            .
          </p>
          <p className='mb-[10px]'>
            Withdrawing your consent: If we are relying on your consent to
            process your personal information, which may be express and/or
            implied consent depending on the applicable law, you have the right
            to withdraw your consent at any time. You can withdraw your consent
            at any time by contacting us by using the contact details provided
            in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below or
            updating your preferences.
          </p>
          <p className='mb-[10px]'>
            However, please note that this will not affect the lawfulness of the
            processing before its withdrawal, nor when applicable law allows,
            will it affect the processing of your personal information conducted
            in reliance on lawful processing grounds other than consent.
          </p>
          <p className='mb-[10px]'>
            Opting out of marketing and promotional communications: You can
            unsubscribe from our marketing and promotional communications at any
            time by clicking on the unsubscribe link in the emails that we send,
            or by contacting us using the details provided in the section "HOW
            CAN YOU CONTACT US ABOUT THIS NOTICE?" below. You will then be
            removed from the marketing lists. However, we may still communicate
            with you — for example, to send you service-related messages that
            are necessary for the administration and use of your account, to
            respond to service requests, or for other non-marketing purposes.
          </p>
          <p className='mb-[10px]'>Account Information</p>
          <p className='mb-[10px]'>
            If you would at any time like to review or change the information in
            your account or terminate your account, you can:Contact us using the
            contact information provided.Log in to your account settings and
            update your user account.Upon your request to terminate your
            account, we will deactivate or delete your account and information
            from our active databases. However, we may retain some information
            in our files to prevent fraud, troubleshoot problems, assist with
            any investigations, enforce our legal terms and/or comply with
            applicable legal requirements.
          </p>
          <p className='mb-[10px]'>
            Cookies and similar technologies: Most Web browsers are set to
            accept cookies by default. If you prefer, you can usually choose to
            set your browser to remove cookies and to reject cookies. If you
            choose to remove cookies or reject cookies, this could affect
            certain features or services of our Services. To opt out of
            interest-based advertising by advertisers on our Services visit
            <a
              className='text-azure-455 underline'
              href='http://www.aboutads.info/choices/'
            >
              http://www.aboutads.info/choices/
            </a>
            .
          </p>
          <p className='mb-[10px]'>
            If you have questions or comments about your privacy rights, you may
            email us at
            <a
              className='text-azure-455 underline'
              href='mailto:support@listening.com'
            >
              support@listening.com
            </a>
            .
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>11.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='11-controls-for-do-not-track'
            >
              CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
          </div>

          <p className='mb-[10px]'>
            Most web browsers and some mobile operating systems and mobile
            applications include a Do-Not-Track ("DNT") feature or setting you
            can activate to signal your privacy preference not to have data
            about your online browsing activities monitored and collected. At
            this stage no uniform technology standard for recognizing and
            implementing DNT signals has been finalized. As such, we do not
            currently respond to DNT browser signals or any other mechanism that
            automatically communicates your choice not to be tracked online. If
            a standard for online tracking is adopted that we must follow in the
            future, we will inform you about that practice in a revised version
            of this privacy notice.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>12.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='12-do-california-residents-have-specifc-rights'
            >
              DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: Yes, if you are a resident of California, you are granted
            specific rights regarding access to your personal information.
          </p>
          <p className='mb-[10px]'>
            California Civil Code Section 1798.83, also known as the "Shine The
            Light" law, permits our users who are California residents to
            request and obtain from us, once a year and free of charge,
            information about categories of personal information (if any) we
            disclosed to third parties for direct marketing purposes and the
            names and addresses of all third parties with which we shared
            personal information in the immediately preceding calendar year. If
            you are a California resident and would like to make such a request,
            please submit your request in writing to us using the contact
            information provided below.
          </p>
          <p className='mb-[10px]'>
            If you are under 18 years of age, reside in California, and have a
            registered account with Services, you have the right to request
            removal of unwanted data that you publicly post on the Services. To
            request removal of such data, please contact us using the contact
            information provided below and include the email address associated
            with your account and a statement that you reside in California. We
            will make sure the data is not publicly displayed on the Services,
            but please be aware that the data may not be completely or
            comprehensively removed from all our systems (e.g., backups, etc.).
          </p>
          <p className='mb-[10px]'>CCPA Privacy Notice</p>
          <p className='mb-[10px]'>
            The California Code of Regulations defines a "resident" as:
          </p>
          <p className='mb-[10px]'>
            (1) every individual who is in the State of California for other
            than a temporary or transitory purpose and(2) every individual who
            is domiciled in the State of California who is outside the State of
            California for a temporary or transitory purpose
          </p>
          <p className='mb-[10px]'>
            All other individuals are defined as "non-residents."
          </p>
          <p className='mb-[10px]'>
            If this definition of "resident" applies to you, we must adhere to
            certain rights and obligations regarding your personal information.
          </p>
          <p className='mb-[10px]'>
            What categories of personal information do we collect?
          </p>
          <p className='mb-[10px]'>
            We have collected the following categories of personal information
            in the past twelve (12) months:
          </p>
          <p className='mb-[10px]'>
            Category Examples CollectedA. IdentifiersContact details, such as
            real name, alias, postal address, telephone or mobile contact
            number, unique personal identifier, online identifier, Internet
            Protocol address, email address, and account name
          </p>
          <p className='mb-[10px]'>YES</p>
          <p className='mb-[10px]'>
            B. Personal information categories listed in the California Customer
            Records statuteName, contact information, education, employment,
            employment history, and financial information
          </p>
          <p className='mb-[10px]'>YES</p>
          <p className='mb-[10px]'>
            C. Protected classification characteristics under California or
            federal lawGender and date of birth
          </p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            D. Commercial informationTransaction information, purchase history,
            financial details, and payment information
          </p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            E. Biometric informationFingerprints and voiceprints
          </p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            F. Internet or other similar network activityBrowsing history,
            search history, online behavior, interest data, and interactions
            with our and other websites, applications, systems, and
            advertisements
          </p>
          <p className='mb-[10px]'>YES</p>
          <p className='mb-[10px]'>G. Geolocation dataDevice location</p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            H. Audio, electronic, visual, thermal, olfactory, or similar
            informationImages and audio, video or call recordings created in
            connection with our business activities
          </p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            I. Professional or employment-related informationBusiness contact
            details in order to provide you our services at a business level or
            job title, work history, and professional qualifications if you
            apply for a job with us
          </p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            J. Education InformationStudent records and directory information
          </p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            K. Inferences drawn from other personal informationInferences drawn
            from any of the collected personal information listed above to
            create a profile or summary about, for example, an individual’s
            preferences and characteristics
          </p>
          <p className='mb-[10px]'>NO</p>
          <p className='mb-[10px]'>
            We may also collect other personal information outside of these
            categories instances where you interact with us in person, online,
            or by phone or mail in the context of:Receiving help through our
            customer support channels;Participation in customer surveys or
            contests; andFacilitation in the delivery of our Services and to
            respond to your inquiries.How do we use and share your personal
            information?
          </p>
          <p className='mb-[10px]'>
            More information about our data collection and sharing practices can
            be found in this privacy notice.
          </p>
          <p className='mb-[10px]'>
            You may contact us by email at
            <a
              className='text-azure-455 underline'
              href='mailto:support@listening.com'
            >
              support@listening.com
            </a>
            , or by referring to the contact details at the bottom of this
            document.
          </p>
          <p className='mb-[10px]'>
            If you are using an authorized agent to exercise your right to opt
            out we may deny a request if the authorized agent does not submit
            proof that they have been validly authorized to act on your behalf.
          </p>
          <p className='mb-[10px]'>
            Will your information be shared with anyone else?
          </p>
          <p className='mb-[10px]'>
            We may disclose your personal information with our service providers
            pursuant to a written contract between us and each service provider.
            Each service provider is a for-profit entity that processes the
            information on our behalf.
          </p>
          <p className='mb-[10px]'>
            We may use your personal information for our own business purposes,
            such as for undertaking internal research for technological
            development and demonstration. This is not considered to be
            "selling" of your personal information.
          </p>
          <p className='mb-[10px]'>
            Listening.com has not disclosed or sold any personal information to
            third parties for a business or commercial purpose in the preceding
            twelve (12) months. Listening.com will not sell personal information
            in the future belonging to website visitors, users, and other
            consumers.
          </p>
          <p className='mb-[10px]'>
            Your rights with respect to your personal data
          </p>
          <p className='mb-[10px]'>
            Right to request deletion of the data — Request to delete
          </p>
          <p className='mb-[10px]'>
            You can ask for the deletion of your personal information. If you
            ask us to delete your personal information, we will respect your
            request and delete your personal information, subject to certain
            exceptions provided by law, such as (but not limited to) the
            exercise by another consumer of his or her right to free speech, our
            compliance requirements resulting from a legal obligation, or any
            processing that may be required to protect against illegal
            activities.
          </p>
          <p className='mb-[10px]'>Right to be informed — Request to know</p>
          <p className='mb-[10px]'>
            Depending on the circumstances, you have a right to know:whether we
            collect and use your personal information;the categories of personal
            information that we collect;the purposes for which the collected
            personal information is used;whether we sell your personal
            information to third parties;the categories of personal information
            that we sold or disclosed for a business purpose;the categories of
            third parties to whom the personal information was sold or disclosed
            for a business purpose; andthe business or commercial purpose for
            collecting or selling personal information.In accordance with
            applicable law, we are not obligated to provide or delete consumer
            information that is de-identified in response to a consumer request
            or to re-identify individual data to verify a consumer request.
          </p>
          <p className='mb-[10px]'>
            Right to Non-Discrimination for the Exercise of a Consumer’s Privacy
            Rights
          </p>
          <p className='mb-[10px]'>
            We will not discriminate against you if you exercise your privacy
            rights.
          </p>
          <p className='mb-[10px]'>Verification process</p>
          <p className='mb-[10px]'>
            Upon receiving your request, we will need to verify your identity to
            determine you are the same person about whom we have the information
            in our system. These verification efforts require us to ask you to
            provide information so that we can match it with information you
            have previously provided us. For instance, depending on the type of
            request you submit, we may ask you to provide certain information so
            that we can match the information you provide with the information
            we already have on file, or we may contact you through a
            communication method (e.g., phone or email) that you have previously
            provided to us. We may also use other verification methods as the
            circumstances dictate.
          </p>
          <p className='mb-[10px]'>
            We will only use personal information provided in your request to
            verify your identity or authority to make the request. To the extent
            possible, we will avoid requesting additional information from you
            for the purposes of verification. However, if we cannot verify your
            identity from the information already maintained by us, we may
            request that you provide additional information for the purposes of
            verifying your identity and for security or fraud-prevention
            purposes. We will delete such additionally provided information as
            soon as we finish verifying you.
          </p>
          <p className='mb-[10px]'>
            Other privacy rightsYou may object to the processing of your
            personal information.You may request correction of your personal
            data if it is incorrect or no longer relevant, or ask to restrict
            the processing of the information.You can designate an authorized
            agent to make a request under the CCPA on your behalf. We may deny a
            request from an authorized agent that does not submit proof that
            they have been validly authorized to act on your behalf in
            accordance with the CCPA.You may request to opt out from future
            selling of your personal information to third parties. Upon
            receiving an opt-out request, we will act upon the request as soon
            as feasibly possible, but no later than fifteen (15) days from the
            date of the request submission.To exercise these rights, you can
            contact us by email at
            <a
              className='text-azure-455 underline'
              href='mailto:support@listening.com'
            >
              support@listening.com
            </a>
            , or by referring to the contact details at the bottom of this
            document. If you have a complaint about how we handle your data, we
            would like to hear from you.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>13.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='13-do-we-make-updates-to-this-notice'
            >
              DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
          </div>

          <p className='mb-[10px]'>
            In Short: Yes, we will update this notice as necessary to stay
            compliant with relevant laws.
          </p>
          <p className='mb-[10px]'>
            We may update this privacy notice from time to time. The updated
            version will be indicated by an updated "Revised" date and the
            updated version will be effective as soon as it is accessible. If we
            make material changes to this privacy notice, we may notify you
            either by prominently posting a notice of such changes or by
            directly sending you a notification. We encourage you to review this
            privacy notice frequently to be informed of how we are protecting
            your information.
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>14.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='14-how-can-you-contact-us-about-this-notice'
            >
              HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
          </div>

          <p className='mb-[10px]'>
            If you have questions or comments about this notice, you may contact
            our Data Protection Officer (DPO), Derek Pankaew, by email at
            <a
              className='text-azure-455 underline'
              href='mailto:support@listening.com'
            >
              support@listening.com
            </a>
            .
          </p>

          <div className='flex list-decimal items-center pl-6'>
            <span className='pr-1'>15.</span>
            <h2
              className='text-[0.875rem] leading-[1.85] font-bold'
              id='15-how-can-you-review-update-or-delete'
            >
              HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
              YOU?
            </h2>
          </div>

          <p className='mb-[10px]'>
            Based on the applicable laws of your country, you may have the right
            to request access to the personal information we collect from you,
            change that information, or delete it. To request to review, update,
            or delete your personal information, please visit:
            app.listening.com.
          </p>
        </div>
      </div>
      <Footer />
    </>
  ),
});
